import { useMemo } from "react";
import { motion } from "framer-motion";
import { Ruler, ArrowUpRight, Plus, Check } from "lucide-react";
import { PRICING, STYLE_FACTORS, ADDONS } from "@/lib/content";
import { Slider } from "@/components/ui/slider";

const fmt = (n) => "$" + (Math.round(n / 5) * 5).toLocaleString();

export default function Estimator({ len, setLen, styleId, setStyleId, addons, toggleAddon, onBook }) {
  const factor = STYLE_FACTORS.find((s) => s.id === styleId)?.factor ?? 1;

  const addTotals = useMemo(() => {
    const sel = ADDONS.filter((a) => addons.includes(a.id));
    return {
      low: sel.reduce((s, a) => s + a.low, 0),
      high: sel.reduce((s, a) => s + a.high, 0),
    };
  }, [addons]);

  const estimates = useMemo(
    () =>
      PRICING.map((t) => {
        const base = t.rate * len * factor;
        const low = base + addTotals.low;
        const high = base * 1.25 + addTotals.high;
        return { id: t.id, name: t.name, low: fmt(low), high: fmt(high), popular: t.popular };
      }),
    [len, factor, addTotals]
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9 }}
      className="mb-10 border border-gold/25 bg-white/[0.02] p-6 md:p-10"
      data-testid="price-estimator"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
        {/* Controls */}
        <div className="lg:col-span-5">
          <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold">
            <Ruler size={14} /> Instant estimate
          </p>
          <h3 className="mt-4 font-serif text-2xl md:text-3xl text-white">Size up your quote</h3>

          <div className="mt-8">
            <div className="flex items-end justify-between">
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Boat length</label>
              <span className="font-serif text-3xl text-gold" data-testid="estimator-length">{len}′</span>
            </div>
            <Slider
              className="mt-4"
              value={[len]}
              min={14}
              max={60}
              step={1}
              onValueChange={([v]) => setLen(v)}
              data-testid="estimator-slider"
            />
            <div className="mt-2 flex justify-between text-[10px] uppercase tracking-[0.2em] text-white/30">
              <span>14′</span>
              <span>60′</span>
            </div>
          </div>

          <div className="mt-8">
            <label className="mb-3 block text-xs uppercase tracking-[0.2em] text-white/50">Boat style / condition</label>
            <div className="flex flex-wrap gap-2">
              {STYLE_FACTORS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setStyleId(s.id)}
                  className={`px-3 py-2 text-xs border transition-colors duration-200 ${
                    styleId === s.id ? "border-gold bg-gold/[0.08] text-white" : "border-white/15 text-white/60 hover:border-white/40"
                  }`}
                  data-testid={`estimator-style-${s.id}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8">
            <label className="mb-3 block text-xs uppercase tracking-[0.2em] text-white/50">Add-ons</label>
            <div className="flex flex-wrap gap-2">
              {ADDONS.map((a) => {
                const on = addons.includes(a.id);
                return (
                  <button
                    key={a.id}
                    onClick={() => toggleAddon(a.id)}
                    className={`inline-flex items-center gap-2 px-3 py-2 text-xs border transition-colors duration-200 ${
                      on ? "border-gold bg-gold/[0.08] text-white" : "border-white/15 text-white/60 hover:border-white/40"
                    }`}
                    data-testid={`estimator-addon-${a.id}`}
                  >
                    {on ? <Check size={12} className="text-gold" /> : <Plus size={12} className="text-white/40" />}
                    {a.label}
                    <span className="text-gold/70">+{fmt(a.low)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="lg:col-span-7">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {estimates.map((e) => (
              <div
                key={e.id}
                className={`p-5 border ${e.popular ? "border-gold bg-gold/[0.05]" : "border-white/10 bg-white/[0.02]"}`}
                data-testid={`estimator-result-${e.id}`}
              >
                <p className="text-xs uppercase tracking-[0.2em] text-white/50">{e.name}</p>
                <p className="mt-3 font-serif text-2xl text-white leading-tight">
                  {e.low}
                  <span className="text-white/40"> – </span>
                  {e.high}
                </p>
                <p className="mt-1 text-[10px] uppercase tracking-[0.15em] text-gold/70">Estimated</p>
              </div>
            ))}
          </div>
          <p className="mt-5 text-sm font-light text-white/45 leading-relaxed">
            A starting estimate based on length and boat type. Larger vessels, heavy oxidation, towers,
            and extensive interiors land toward the upper range — we confirm your exact price with a
            free assessment.
          </p>
          <button
            onClick={onBook}
            className="group mt-6 inline-flex items-center gap-2 bg-gold text-black px-9 py-4 text-xs uppercase tracking-[0.25em] hover:bg-gold-light transition-colors duration-300"
            data-testid="estimator-book"
          >
            Get my exact quote
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}
